// fetch crm accounts
// Cache for storing fetched accounts data
let accountsCache = {
  data: [],
  timestamp: null,
  isLoading: false,
  pendingPromise: null,
  searchResults: {},
};

// Cache expiration time in milliseconds (5 minutes)
const CACHE_EXPIRY = 5 * 60 * 1000;
// Search cache expiration time (1 minute)
const SEARCH_CACHE_EXPIRY = 1 * 60 * 1000;

// Global loading state handler
let loadingHandlers = [];
let searchTimeoutId = null;

// Register a loading state handler
export const registerAccountsLoadingHandler = (handler) => {
  loadingHandlers.push(handler);
  return () => {
    loadingHandlers = loadingHandlers.filter((h) => h !== handler);
  };
};

// Update all registered handlers with the current loading state
export const updateLoadingState = (isLoading, message = null) => {
  loadingHandlers.forEach((handler) => handler(isLoading, message));
};

// Check if ZOHO SDK is available
const isZohoAvailable = () => {
  return window.ZOHO && window.ZOHO.CRM && window.ZOHO.CRM.API;
};

// Function to fetch initial accounts (first page only)
export const fetchCRMAccounts = async (forceRefresh = false) => {
  // First check if ZOHO SDK is available
  if (!isZohoAvailable()) {
    console.warn("⚠️ ZOHO CRM API not available. Cannot fetch accounts.");
    return Promise.resolve([]);
  }

  // Return cached data if available and not expired and not forced to refresh
  const now = Date.now();
  if (
    !forceRefresh &&
    accountsCache.data.length > 0 &&
    accountsCache.timestamp &&
    now - accountsCache.timestamp < CACHE_EXPIRY
  ) {
    console.log(
      "Returning cached accounts data:",
      accountsCache.data.length,
      "records"
    );
    return accountsCache.data;
  }

  // If a request is already in progress, return the pending promise
  if (accountsCache.isLoading && accountsCache.pendingPromise) {
    console.log("Request already in progress, waiting for it to complete");
    return accountsCache.pendingPromise;
  }

  // Set loading state
  accountsCache.isLoading = true;
  updateLoadingState(true, "Loading accounts data...");

  // Create and store the promise - ONLY LOAD FIRST PAGE
  accountsCache.pendingPromise = new Promise(async (resolve, reject) => {
    try {
      console.log("Fetching initial accounts data (first page only)...");
      updateLoadingState(true, `Loading initial accounts...`);

      // Using ZOHO.CRM.API to fetch accounts - first page only
      const response = await ZOHO.CRM.API.getAllRecords({
        Entity: "Accounts",
        sort_order: "asc",
        per_page: 200,
        page: 1,
      });

      console.log(`✅ Initial Accounts API Response:`, response);

      if (!response || !Array.isArray(response.data)) {
        console.error(`❌ Invalid accounts response structure:`, response);
        resolve([]);
        return;
      }

      const accounts = response.data.map((account) => ({
        id: account.id,
        name: account.Account_Name || "Unnamed Account",
        rawData: account, // Keep raw data for reference if needed
      }));

      console.log(`✅ Initial accounts fetched: ${accounts.length}`);

      // Update cache
      accountsCache.data = accounts;
      accountsCache.timestamp = Date.now();

      // Add a small delay to show the loader even for quick responses
      setTimeout(() => {
        updateLoadingState(false);
        resolve(accounts);
      }, 300);
    } catch (error) {
      console.error("❌ Error fetching accounts:", error);
      resolve([]);
    } finally {
      // Clear loading state (after timeout)
      setTimeout(() => {
        accountsCache.isLoading = false;
        accountsCache.pendingPromise = null;
        updateLoadingState(false);
      }, 300);
    }
  });

  return accountsCache.pendingPromise;
};

// NEW: Function to search accounts by name directly from API
export const searchAccountsByName = async (searchTerm) => {
  if (
    !searchTerm ||
    typeof searchTerm !== "string" ||
    searchTerm.trim() === ""
  ) {
    return fetchCRMAccounts(); // Return initial accounts if no search term
  }

  // Check if ZOHO SDK is available
  if (!isZohoAvailable()) {
    console.warn("⚠️ ZOHO CRM API not available. Cannot search accounts.");
    return [];
  }

  const normalizedSearchTerm = searchTerm.toLowerCase().trim();

  // Check if search results are in cache and not expired
  const now = Date.now();
  if (
    accountsCache.searchResults[normalizedSearchTerm] &&
    accountsCache.searchResults[normalizedSearchTerm].timestamp &&
    now - accountsCache.searchResults[normalizedSearchTerm].timestamp <
      SEARCH_CACHE_EXPIRY
  ) {
    console.log(
      `Returning cached search results for "${normalizedSearchTerm}"`
    );
    return accountsCache.searchResults[normalizedSearchTerm].data;
  }

  // Clear any pending search timeout
  if (searchTimeoutId) {
    clearTimeout(searchTimeoutId);
  }

  // Set loading state
  updateLoadingState(true, `Searching accounts for "${searchTerm}"...`);

  // Create a promise for the search
  return new Promise((resolve) => {
    // Throttle API calls with a 300ms debounce
    searchTimeoutId = setTimeout(async () => {
      try {
        console.log(`Searching accounts for "${normalizedSearchTerm}"...`);

        // Use search criteria for Account_Name field
        const response = await ZOHO.CRM.API.searchRecord({
          Entity: "Accounts",
          Type: "word",
          Query: normalizedSearchTerm,
          Fields: "Account_Name",
          page: 1,
          per_page: 100, // Limit to 100 for better performance
        });

        if (!response || !Array.isArray(response.data)) {
          console.log(`No accounts found for "${normalizedSearchTerm}"`);
          updateLoadingState(false);
          resolve([]);
          return;
        }

        console.log(
          `✅ Found ${response.data.length} accounts for "${normalizedSearchTerm}"`
        );

        const searchResults = response.data.map((account) => ({
          id: account.id,
          name: account.Account_Name || "Unnamed Account",
          rawData: account,
        }));

        // Cache the search results
        accountsCache.searchResults[normalizedSearchTerm] = {
          data: searchResults,
          timestamp: Date.now(),
        };

        // Return the search results
        updateLoadingState(false);
        resolve(searchResults);
      } catch (error) {
        console.error(
          `❌ Error searching accounts for "${normalizedSearchTerm}":`,
          error
        );
        updateLoadingState(false);
        resolve([]);
      }
    }, 300); // 300ms debounce
  });
};

// Function to filter accounts by search term (for local filtering of already fetched accounts)
export const filterAccountsBySearchTerm = (accounts, searchTerm) => {
  if (
    !searchTerm ||
    typeof searchTerm !== "string" ||
    !accounts ||
    accounts.length === 0
  ) {
    return accounts;
  }

  const normalizedSearchTerm = searchTerm.toLowerCase().trim();

  // First prioritize accounts that start with the search term
  const startsWithMatches = accounts.filter(
    (account) =>
      account.name &&
      account.name.toLowerCase().startsWith(normalizedSearchTerm)
  );

  // Then include accounts that contain the search term elsewhere
  const containsMatches = accounts.filter(
    (account) =>
      account.name &&
      account.name.toLowerCase().includes(normalizedSearchTerm) &&
      !account.name.toLowerCase().startsWith(normalizedSearchTerm)
  );

  // Combine both results: first the "starts with" matches, then the "contains" matches
  return [...startsWithMatches, ...containsMatches];
};

// Function to search account by name in cache (efficient for large datasets)
export const searchAccountByName = (searchTerm) => {
  if (
    !searchTerm ||
    typeof searchTerm !== "string" ||
    accountsCache.data.length === 0
  ) {
    return null;
  }

  const normalizedSearchTerm = searchTerm.toLowerCase().trim();

  const matchingAccount = accountsCache.data.find(
    (account) =>
      account.name && account.name.toLowerCase().includes(normalizedSearchTerm)
  );

  if (matchingAccount) {
    console.log(`✅ Found account matching "${searchTerm}":`, matchingAccount);
  } else {
    console.log(`❌ No account found matching "${searchTerm}"`);
  }

  return matchingAccount || null;
};

// Function to invalidate cache (use after updates)
export const invalidateAccountsCache = () => {
  console.log("Invalidating accounts cache");
  accountsCache.timestamp = null;
  accountsCache.searchResults = {};
};
